"use client";

import { useRouter } from "next/navigation";
import React, { useState } from "react";
import { useForm } from "react-hook-form";

import { scrollToTop } from "@/app/(main)/(club)/mes-clubs/[id]/leads/utils";
import { updateClub } from "@/app/api/dynabuy-back/api-club";
import Button from "@/components/button";
import AddressAutocompleteInput, {
  AddressFeature,
} from "@/components/form/inputs/address-autocomplete-input";
import ClientFlash from "@/components/global/client-flash";
import HocTextInput from "@/components/global/inputs/hoc-text-input";
import TextInput from "@/components/global/inputs/text-input";
import MainTitle from "@/components/global/main-title";
import useSubmit from "@/hooks/use-submit";
import { Club } from "@/TS/Interfaces/global_interfaces";
import { handleErrors } from "@/utils/formFunc/form-func";

function EditOpenMap(props: { club: Pick<Club, "postalAddress" | "slug"> }) {
  const router = useRouter();
  const { register, setValue, handleSubmit } = useForm({
    defaultValues: {
      postalAddress: {
        place: props.club.postalAddress?.place ?? "",
        street: props.club.postalAddress?.street ?? "",
        zipCode: props.club.postalAddress?.zipCode ?? "",
        city: props.club.postalAddress?.city ?? "",
        latitude: props.club.postalAddress?.latitude
          ? parseFloat(String(props.club.postalAddress.latitude))
          : undefined,
        longitude: props.club.postalAddress?.longitude
          ? parseFloat(String(props.club.postalAddress.longitude))
          : undefined,
      },
    },
  });

  const { errors, loading, onSubmit, setVal } = useSubmit(updateClub, {
    callbackMessage: {
      message: "La modification a été appliquée avec succès !",
      type: "success2",
      position: "id_page",
    },
    callback: () => {
      router.refresh();
      scrollToTop();
    },
    sidebarJsx: "",
    errorFlashPath: "/mes-clubs#location",
  });

  const [addressInput, setAddressInput] = useState(
    [
      props.club.postalAddress?.street,
      props.club.postalAddress?.zipCode,
      props.club.postalAddress?.city,
    ]
      .filter(Boolean)
      .join(" "),
  );

  const EnhancedTextInput = HocTextInput(TextInput, loading, register);

  const handleAddressSelect = (feature: AddressFeature) => {
    setValue(
      "postalAddress.street",
      feature.properties.name || feature.properties.street || "",
    );
    setValue("postalAddress.zipCode", feature.properties.postcode);
    setValue("postalAddress.city", feature.properties.city);
    setValue("postalAddress.latitude", feature.geometry.coordinates[1]);
    setValue("postalAddress.longitude", feature.geometry.coordinates[0]);
  };

  return (
    <form
      className={"min-h-dvh relative contents"}
      onSubmit={handleSubmit((data) => {
        onSubmit(data, props.club.slug);
      })}
    >
      <div className="flex flex-col w-full mx-auto items-center justify-start min-h-full">
        <MainTitle
          className={"text-center mb-4 w-full self-center justify-self-center"}
          title={"Modifier le lieu de mes réunions Club d'Affaires"}
        ></MainTitle>
        <div className="flex flex-col md:w-2/4 gap-4 w-full">
          <EnhancedTextInput
            error={handleErrors(errors, "postalAddress.place")}
            name={"postalAddress.place"}
            id={"place"}
            required={true}
            needed={false}
            placeholder={"Saisir un nom de lieu"}
            label={"LIEU"}
            labelClassName={"font-bold"}
          />
          <AddressAutocompleteInput
            value={addressInput}
            onChange={setAddressInput}
            onSelect={handleAddressSelect}
            label="ADRESSE"
            disabled={loading}
          />
        </div>
        <ClientFlash path={"/mes-clubs#location"} />
        <div className="flex md:flex-row justify-center w-full flex-col gap-6 mt-10">
          <Button
            onClick={() => setVal("")}
            variant={"unfilled"}
            className={"md:w-56 w-full"}
            colorTheme={"secondary"}
          >
            Annuler
          </Button>
          <Button
            type={"submit"}
            variant={"filled"}
            className={"md:w-56 w-full"}
            colorTheme={"primary"}
            disabled={loading}
          >
            Valider
          </Button>
        </div>
      </div>
    </form>
  );
}

export default EditOpenMap;
